// ═══════════════════════════════════════════════════════════════════════════════
// Employer inventory task wizard — final "Review" step summary card (create / edit)
// ═══════════════════════════════════════════════════════════════════════════════

import type { KeyboardEvent } from 'react';
import { AppButton } from '../ui/AppButton';

export type InventoryWizardReviewRow = {
  id: string;
  label: string;
  valueText: string;
  valueVariant: 'default' | 'empty';
  /** Preserve line breaks for multi-line user text */
  preserveWhitespace?: boolean;
  /** Optional edit action shown beside the row label (e.g. custom task title) */
  onEdit?: () => void;
  editAriaLabel?: string;
};

type BuildReviewRowsInput = {
  t: (key: string) => string;
  title: string;
  description: string;
  categories: string[];
  locations: string[];
  recurrenceSummaryText: string;
  dueTime: string;
  notifyEmployer: boolean;
  notifyExpiryLeadDays: number[];
};

export const buildInventoryWizardReviewRows = ({
  t,
  title,
  description,
  categories,
  locations,
  recurrenceSummaryText,
  dueTime,
  notifyEmployer,
  notifyExpiryLeadDays,
}: BuildReviewRowsInput): InventoryWizardReviewRow[] => {
  const none = t('task.inventory.wizard.summaryNone');
  const row = (
    id: string,
    labelKey: string,
    valueText: string,
    isEmpty: boolean,
    preserveWhitespace?: boolean,
  ): InventoryWizardReviewRow => ({
    id,
    label: t(labelKey),
    valueText,
    valueVariant: isEmpty ? 'empty' : 'default',
    preserveWhitespace,
  });
  const titleTrimmed = title.trim();
  const descTrimmed = description.trim();
  const dueTrimmed = dueTime.trim();
  const categoriesJoined = categories.length > 0 ? categories.join('、') : '';
  const locationsJoined = locations.length > 0 ? locations.join('、') : '';
  const expiryJoined = notifyExpiryLeadDays.length > 0 ? notifyExpiryLeadDays.join(', ') : '';

  return [
    row('rev-title', 'task.inventory.wizard.summaryTitle', titleTrimmed !== '' ? titleTrimmed : none, titleTrimmed === ''),
    row(
      'rev-desc',
      'task.inventory.wizard.summaryDesc',
      descTrimmed !== '' ? descTrimmed : none,
      descTrimmed === '',
      true,
    ),
    row(
      'rev-categories',
      'task.inventory.wizard.summaryCategories',
      categoriesJoined !== '' ? categoriesJoined : none,
      categoriesJoined === '',
    ),
    row(
      'rev-locations',
      'task.inventory.wizard.summaryLocations',
      locationsJoined !== '' ? locationsJoined : none,
      locationsJoined === '',
    ),
    row('rev-schedule', 'task.inventory.wizard.summaryRecurrence', recurrenceSummaryText, false),
    row('rev-duetime', 'task.inventory.wizard.summaryDueTime', dueTrimmed !== '' ? dueTrimmed : none, dueTrimmed === ''),
    row(
      'rev-notify',
      'task.inventory.wizard.summaryNotify',
      notifyEmployer ? t('task.inventory.wizard.summaryNotifyOn') : t('task.inventory.wizard.summaryNotifyOff'),
      false,
    ),
    row(
      'rev-expiry',
      'task.inventory.wizard.summaryExpiryLeads',
      expiryJoined !== '' ? expiryJoined : none,
      expiryJoined === '',
    ),
  ];
};

type InventoryWizardReviewStepProps = {
  cardHeading: string;
  rows: InventoryWizardReviewRow[];
  backLabel: string;
  primaryLabel: string;
  primaryBusyLabel: string;
  isPrimaryBusy: boolean;
  isBackDisabled: boolean;
  isPrimaryDisabled: boolean;
  onBack: () => void;
  onPrimary: () => void;
};

const handleButtonKeyDown =
  (action: () => void, disabled: boolean) =>
  (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (disabled) {
      return;
    }
    if (e.key !== 'Enter' && e.key !== ' ') {
      return;
    }
    e.preventDefault();
    action();
  };

export const InventoryWizardReviewStep = ({
  cardHeading,
  rows,
  backLabel,
  primaryLabel,
  primaryBusyLabel,
  isPrimaryBusy,
  isBackDisabled,
  isPrimaryDisabled,
  onBack,
  onPrimary,
}: InventoryWizardReviewStepProps) => {
  const primaryDisplay = isPrimaryBusy ? primaryBusyLabel : primaryLabel;

  return (
    <section
      className="rounded-2xl border border-gray-200/40 bg-white p-6 shadow-sm dark:border-gray-700/80 dark:bg-gray-900"
      aria-label={cardHeading}
    >
      <h2 className="mb-6 text-[15px] font-bold text-gray-900 dark:text-gray-100">{cardHeading}</h2>
      <div className="space-y-6">
        {rows.map((row) => (
          <div
            key={row.id}
            className="flex flex-col gap-1.5 border-b-2 border-gray-300 pb-4 last:border-b-0 last:pb-0 dark:border-gray-600"
          >
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-extrabold uppercase tracking-widest text-primary-700 dark:text-primary-400">
                {row.label}
              </span>
              {row.onEdit ? (
                <button
                  type="button"
                  onClick={row.onEdit}
                  onKeyDown={handleButtonKeyDown(row.onEdit, false)}
                  tabIndex={0}
                  aria-label={row.editAriaLabel ?? row.label}
                  className="min-h-8 min-w-8 rounded-lg px-1 text-primary-600 transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-950/40"
                >
                  ✎
                </button>
              ) : null}
            </div>
            <span
              className={
                row.valueVariant === 'empty'
                  ? `text-[15px] font-semibold leading-tight italic text-gray-500 dark:text-gray-400${row.preserveWhitespace ? ' whitespace-pre-wrap' : ''}`
                  : `text-[15px] font-semibold leading-tight text-[#1A202C] dark:text-gray-100${row.preserveWhitespace ? ' whitespace-pre-wrap' : ''}`
              }
            >
              {row.valueText}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-10 flex gap-3">
        <AppButton
          type="button"
          variant="secondary"
          size="lg"
          onClick={onBack}
          onKeyDown={handleButtonKeyDown(onBack, isBackDisabled)}
          disabled={isBackDisabled}
          tabIndex={0}
          aria-label={backLabel}
          className="flex-1"
        >
          {backLabel}
        </AppButton>
        <AppButton
          type="button"
          variant="primary"
          size="lg"
          onClick={onPrimary}
          onKeyDown={handleButtonKeyDown(onPrimary, isPrimaryDisabled)}
          disabled={isPrimaryDisabled}
          tabIndex={0}
          aria-busy={isPrimaryBusy}
          aria-label={primaryDisplay}
          className="flex-[1.5]"
        >
          {primaryDisplay}
        </AppButton>
      </div>
    </section>
  );
};
